import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import {
  AgGridTextTooltip,
  FADED_CELL_CLASSNAME,
  formattedUTCDay,
  isPricingModeRelative,
  isTerminalQuoteStatus,
  PopoverContent,
  QuoteStatusEnum,
  Stack,
  Text,
  type ColumnDef,
  type CountOfParams,
  type CountOfValue,
  type ExpiryCountdownParams,
  type PriceParams,
  type QuoteMarket,
  type QuoteStatusColumnParams,
  type SizeColumnParams,
  type SpreadColumnParams,
} from '@talos/kyoko';
import type { CellClassRules, ICellRendererParams, ITooltipParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { bidCellClassRules, offerCellClassRules } from './columnClassRules';
import { isPricingRow, QuoteMarketRow, QuoteRow, type QuotesBlotterRow } from './types';
import {
  useQuotesV2BlotterActionColumn,
  type UseQuotesV2BlotterActionColumnParams,
} from './useQuotesV2BlotterActionColumn';

const childRowFadedCellClassRules: CellClassRules<QuotesBlotterRow> = {
  [FADED_CELL_CLASSNAME]: params => params.data instanceof QuoteMarketRow,
};

export const useQuotesV2Columns = ({ onOpenQuote, onShowDetails }: UseQuotesV2BlotterActionColumnParams) => {
  const { enrichRFQBlotterV2WithCareOrders, enableQuotesBidOfferQtyColumns } = useFeatureFlag();
  const actionColumn = useQuotesV2BlotterActionColumn({ onOpenQuote, onShowDetails });

  const columns = useMemo(() => {
    const baseColumns = compact([
      {
        field: 'SubmitTime',
        type: 'date',
        title: 'Submit Time',
        initialSort: '-',
        initialWidth: 130,
        cellClassRules: childRowFadedCellClassRules,
      },
      {
        field: 'expiryTime',
        type: 'expiryCountdown',
        title: 'Expiry',
        initialWidth: 130,
        params: {
          isExpired: (data: unknown) => isPricingRow(data) && isTerminalQuoteStatus(data.QuoteStatus),
        } satisfies ExpiryCountdownParams<QuotesBlotterRow>,
      },
      {
        field: 'quote.RFQID',
        type: 'id',
        title: 'RFQID',
        initialWidth: 80,
        initialHide: true,
      },
      {
        type: 'countOf',
        id: 'responses',
        title: 'Responses',
        description: 'Count of responses received from makers. Only shown for open quotes.',
        initialWidth: 50,
        params: {
          /* Only render anything at all (provide the column with items) if its open for trading */
          getItems: (data: QuotesBlotterRow) =>
            data instanceof QuoteRow && data.QuoteStatus === QuoteStatusEnum.Open ? data.quote.Markets : undefined,
          shouldCount: (market: QuoteMarket) => market.QuoteStatus === QuoteStatusEnum.Open,
        } satisfies CountOfParams<QuotesBlotterRow, QuoteMarket>,
        tooltipValueGetter: ({ value }: ITooltipParams<unknown, CountOfValue<QuoteMarket>>) => value,
        tooltipComponent: (props: ICellRendererParams<unknown, CountOfValue<QuoteMarket> | string>) => {
          if (!props.value) {
            return null;
          }

          if (typeof props.value === 'string') {
            // The headerTooltip ("description") above also uses the column's tooltipComponent, so we have to support this here...
            return <AgGridTextTooltip {...props} />;
          }

          return (
            <PopoverContent>
              <Stack maxWidth="300px" color="colorTextDefault" gap="spacing4">
                <Text>
                  Response:{' '}
                  {props.value.countedItems
                    .map(
                      (qm: QuoteMarket) => props.context.current.marketDisplayNameByName?.get(qm.Market) ?? qm.Market
                    )
                    .join(', ')}
                </Text>

                <Text>
                  No Response:{' '}
                  {props.value.notCountedItems
                    .map(
                      (qm: QuoteMarket) => props.context.current.marketDisplayNameByName?.get(qm.Market) ?? qm.Market
                    )
                    .join(', ')}
                </Text>
              </Stack>
            </PopoverContent>
          );
        },
      },
      {
        field: 'Symbol',
        type: 'security',
        title: 'Symbol',
        initialWidth: 160,
        cellClassRules: childRowFadedCellClassRules,
      },
      {
        field: 'OrderQty',
        type: 'size',
        title: 'Quantity',
        params: { currencyField: 'Currency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        cellClassRules: childRowFadedCellClassRules,
      },
      {
        field: 'quote.TradedQty',
        type: 'size',
        title: 'Traded Qty',
        params: { currencyField: 'quote.Currency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        initialHide: true,
      },
      {
        field: 'Side',
        id: 'Side',
        title: 'Side',
        type: 'quoteSide',
        cellClassRules: childRowFadedCellClassRules,
      },

      // Bid
      {
        type: 'market',
        field: 'bid.Market',
        title: 'Bid Maker',
        initialWidth: 150,
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        cellClassRules: bidCellClassRules,
      },
      enableQuotesBidOfferQtyColumns && {
        type: 'size',
        field: 'bidQty',
        id: 'BidQty',
        title: 'Bid Qty',
        initialWidth: 90,
        params: { currencyField: 'Currency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        cellClassRules: bidCellClassRules,
      },
      {
        type: 'price',
        field: 'bidDisplayPx',
        id: 'BidPx',
        title: 'Bid',
        initialWidth: 80,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingRow(data) && isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingRow(data) && isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
        } satisfies PriceParams<QuotesBlotterRow>,
        cellClassRules: bidCellClassRules,
      },
      // Spread
      {
        id: 'spread',
        type: 'spread',
        title: 'Spread',
        params: {
          bidField: 'bidDisplayPx',
          askField: 'offerDisplayPx',
          pricingModeField: 'PricingMode',
        } satisfies SpreadColumnParams<QuotesBlotterRow>,
      },
      // Offer
      {
        type: 'price',
        field: 'offerDisplayPx',
        title: 'Offer',
        id: 'OfferPx',
        initialWidth: 80,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingRow(data) && isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingRow(data) && isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
          align: 'left',
        } satisfies PriceParams<QuotesBlotterRow>,
        cellClassRules: offerCellClassRules,
      },
      enableQuotesBidOfferQtyColumns && {
        type: 'size',
        field: 'offerQty',
        id: 'OfferQty',
        title: 'Offer Qty',
        initialWidth: 90,
        params: {
          currencyField: 'Currency',
          align: 'left',
          inlineFormattedNumberAlignment: 'left',
        } satisfies SizeColumnParams<QuotesBlotterRow>,
        cellClass: 'ag-left-aligned-cell',
        cellClassRules: offerCellClassRules,
      },
      {
        type: 'market',
        field: 'offer.Market',
        title: 'Offer Maker',
        initialWidth: 150,
        cellClassRules: offerCellClassRules,
      },
      {
        field: 'QuoteStatus',
        type: 'quoteStatus',
        title: 'Status',
        params: {
          tooltipField: 'Text',
        } satisfies QuoteStatusColumnParams<QuotesBlotterRow>,
      },

      {
        field: 'quote.subAccounts',
        type: 'subAccount',
        title: 'Sub Account(s)',
      },
      {
        field: 'quote.User',
        type: 'user',
        title: 'User',
      },

      // Care Order enrichment columns (feature-flagged)
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.OrderID',
        type: 'id',
        initialWidth: 80,
        title: 'Care Order ID',
      },
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.initiatingFrom',
        type: 'text',
        title: 'Initiating From',
        initialWidth: 140,
      },
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.Counterparty',
        type: 'counterparty',
        title: 'Counterparty',
        initialWidth: 180,
      },
      enrichRFQBlotterV2WithCareOrders && {
        field: 'quote.careOrder.fund',
        type: 'text',
        title: 'Fund',
        initialHide: true,
      },
      {
        field: 'units',
        type: 'number',
        title: 'Units',
        initialWidth: 100,
        initialHide: true,
      },

      { field: 'quote.PricingMode', type: 'text', title: 'Pricing Mode', initialHide: true },
      {
        field: 'quote.ParentRFQID',
        title: 'Parent ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.QuoteID',
        title: 'Quote ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.Markets',
        type: 'quoteMarkets',
        title: 'Markets',
        initialHide: true,
      },
      {
        field: 'quote.Timestamp',
        title: 'Timestamp',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.MessageID',
        title: 'Message ID',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Currency',
        title: 'Currency',
        type: 'currency',
        initialHide: true,
      },
      {
        field: 'quote.QuoteReqID',
        title: 'Quote Req ID',
        type: 'id',
        initialHide: true,
      },
      {
        field: 'quote.QuoteRequestRejectReason',
        title: 'Quote Request Reject Reason',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.AmountCurrency',
        title: 'Amount Currency',
        type: 'currency',
        initialHide: true,
      },
      {
        field: 'quote.FirstTransactTime',
        title: 'First Transact Time',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.EndTime',
        title: 'End Time',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.SettleDate',
        title: 'Settlement Date',
        type: 'date',
        params: { customFormatter: formattedUTCDay },
        initialHide: true,
      },
      {
        field: 'quote.ExternalComments',
        title: 'External Comments',
        type: 'text',
        initialHide: true,
      },
      {
        id: 'sizeCountering',
        field: 'sizeCountering',
        title: 'Size Countering',
        type: 'text',
        description: 'Shows if Size Countering is enabled',
        initialHide: true,
      },
      {
        field: 'quote.TradedPx',
        title: 'Traded Px',
        type: 'price',
        params: { assetField: 'quote.Symbol' } satisfies PriceParams<QuotesBlotterRow>,
        initialHide: true,
      },
      {
        field: 'quote.TradedAmt',
        type: 'size',
        title: 'Traded Amt',
        params: { currencyField: 'quote.AmountCurrency' } satisfies SizeColumnParams<QuotesBlotterRow>,
        initialHide: true,
      },
      {
        field: 'quote.TradedSide',
        title: 'Traded Side',
        type: 'side',
        initialHide: true,
      },
      { field: 'quote.tradedMarkets', type: 'markets', title: 'Traded Markets', sortable: true, initialHide: true },
      {
        field: 'quote.TransactTime',
        title: 'Transact Time',
        type: 'date',
        initialHide: true,
      },
      {
        field: 'quote.EventIndicator',
        title: 'Event Indicator',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Text',
        title: 'Text',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Group',
        title: 'Group',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.Parameters',
        title: 'Parameters',
        type: 'text',
        initialHide: true,
      },
      {
        field: 'quote.fixingIndices',
        title: 'Fixing Index',
        type: 'fixingIndices',
        id: 'fixingIndices',
        initialHide: true,
      },
      {
        field: 'quote.FixingDetails',
        id: 'fixingPrice',
        title: 'Fixing Price',
        type: 'fixingPrice',
        initialHide: true,
      },
      {
        field: 'quote.fixingDate',
        id: 'fixingDate',
        title: 'Fixing Date',
        type: 'dateOnly',
        initialHide: true,
      },
    ] satisfies (ColumnDef<QuotesBlotterRow> | false)[]);

    return [...baseColumns, actionColumn];
  }, [enrichRFQBlotterV2WithCareOrders, enableQuotesBidOfferQtyColumns, actionColumn]);

  return columns;
};
